// src/services/soundService.ts
import { loadUserProfile } from './storageService';
//cleberation music from pixaby by Music by <a href="https://pixabay.com/users/backgroundmusicforvideos-46459014/?utm_source=link-attribution&utm_medium=referral&utm_campaign=music&utm_content=334864">Maksym Malko</a> from <a href="https://pixabay.com//?utm_source=link-attribution&utm_medium=referral&utm_campaign=music&utm_content=334864">Pixabay</a>

// Sound map
const sounds: Record<string, HTMLAudioElement> = {};

// Sound keys — mapped to the exact assets in public/sounds.
export type SoundType =
  | 'combine'
  | 'discover'
  | 'select'
  | 'error'
  | 'background'
  | 'celebration'
  | 'coin'
  | 'levelup'
  | 'unlock'
  | 'highscore'
  | 'gameover'
  | 'powerup';

// Default sound enabled state
let soundEnabled = true;
let musicEnabled = true;

// Preload sounds
const preloadSounds = () => {
  // Add all your sound files here
  const soundFiles: Record<SoundType, string> = {
    combine: '/sounds/combo3.mp3',
    discover: '/sounds/discover.mp3',
    select: '/sounds/select.mp3',
    error: '/sounds/error.mp3',
    background: '/sounds/background.mp3',
    celebration: '/sounds/celebrate.mp3',
    coin: '/sounds/coin.mp3',
    levelup: '/sounds/levelup.mp3',
    unlock: '/sounds/unlock.mp3',
    highscore: '/sounds/highscore.mp3',
    gameover: '/sounds/gameover.mp3',
    powerup: '/sounds/powerup.mp3',
  };
  
  // Create audio elements
  Object.entries(soundFiles).forEach(([key, path]) => {
    try {
      const audio = new Audio(path);
      
      // Configure background music
      if (key === 'background') {
        audio.loop = true;
        audio.volume = 0.3;
      } else {
        audio.volume = 0.7;
      }
      
      sounds[key] = audio;
    } catch (error) {
      console.error(`Failed to load sound: ${key}`, error);
    }
  });
};

// Initialize sound system
export const initSounds = () => {
  // Load user preferences
  const profile = loadUserProfile();
  soundEnabled = profile.preferences.soundEffects;
  musicEnabled = profile.preferences.music;
  
  // Preload sounds
  preloadSounds();
  
  // Return cleanup function
  return () => {
    Object.values(sounds).forEach(sound => {
      sound.pause();
      sound.currentTime = 0;
    });
  };
};

// Play a sound
export const playSound = (soundName: SoundType) => {
  if (!sounds[soundName]) return;
  
  // Check if sound is enabled
  if (!soundEnabled && soundName !== 'background') return;
  if (!musicEnabled && soundName === 'background') return;
  
  try {
    const sound = sounds[soundName];
    
    // For non-looping sounds, reset and play
    if (soundName !== 'background') {
      sound.currentTime = 0;
    }
    
    // Play the sound (and handle autoplay restrictions)
    const playPromise = sound.play();
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        // Auto-play was prevented, we'll need user interaction
        console.warn('Sound play prevented:', error);
      });
    }
  } catch (error) {
    console.error(`Error playing sound: ${soundName}`, error);
  }
};

// Stop a sound
export const stopSound = (soundName: SoundType) => {
  if (!sounds[soundName]) return;
  
  try {
    sounds[soundName].pause();
    sounds[soundName].currentTime = 0;
  } catch (error) {
    console.error(`Error stopping sound: ${soundName}`, error);
  }
};

// Toggle sound effects
export const toggleSoundEffects = (enabled: boolean) => {
  soundEnabled = enabled;
};

// Toggle background music
export const toggleMusic = (enabled: boolean) => {
  musicEnabled = enabled;
  
  if (enabled) {
    playSound('background');
  } else {
    stopSound('background');
  }
};

// Update sound preferences based on user profile
export const updateSoundPreferences = () => {
  const profile = loadUserProfile();
  
  // Update settings
  soundEnabled = profile.preferences.soundEffects;
  musicEnabled = profile.preferences.music;
  
  // Apply music setting
  if (musicEnabled) {
    playSound('background');
  } else {
    stopSound('background');
  }
};